import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Upload, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { id: "gallery", label: "Gallery grid" },
  { id: "hero", label: "Hero (top of page)" },
  { id: "about", label: "About section" },
];

const empty = { url: "", label: "", kind: "gallery", position: 0 };

export default function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("gallery");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gallery`);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uploadFile = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);
    let added = 0;
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const { data: item } = await axios.post(`${API}/admin/gallery`, {
            url: up.url,
            label: draft.label || file.name.replace(/\.[^.]+$/, ""),
            kind: draft.kind,
            position: draft.position + added,
          });
          setItems((i) => [...i, item]);
          added += 1;
        } catch (e) {
          const msg = e.response?.data?.detail || e.message;
          toast.error(`${file.name}: ${msg}`);
        }
      }
      if (added > 0) toast.success(`Uploaded ${added} image${added > 1 ? "s" : ""} ✨`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!draft.url) { toast.error("Image URL is required"); return; }
    try {
      const { data } = await axios.post(`${API}/admin/gallery`, draft);
      setItems((i) => [...i, data]);
      setDraft(empty);
      toast.success("Image added ✨");
    } catch (e) {
      toast.error("Add failed");
    }
  };

  const saveEdit = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/admin/gallery/${id}`, patch);
      setItems((i) => i.map((x) => (x.id === id ? data : x)));
      setEditing(null);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`);
      setItems((i) => i.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const shown = items.filter((i) => i.kind === filter).sort((a, b) => a.position - b.position);

  const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  return (
    <div data-testid="gallery-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold text-brand-ink">Website images</h2>
          <p className="text-brand-ink/70">Upload from your device or paste image URLs — changes appear live on the site.</p>
        </div>
        <div className="flex gap-2">
          {KINDS.map((k) => (
            <button
              key={k.id}
              onClick={() => setFilter(k.id)}
              data-testid={`gallery-filter-${k.id}`}
              className={`rounded-full border-2 border-brand-ink px-3 py-1 text-sm font-bold ${filter === k.id ? "bg-brand-red text-white" : "bg-white text-brand-ink hover:bg-brand-yellow/60"}`}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {/* Upload from device */}
      <div className="mt-6 rounded-3xl border-[3px] border-dashed border-brand-ink bg-white p-6 shadow-brutal-sm" data-testid="gallery-upload-zone">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-brand-green border-2 border-brand-ink flex items-center justify-center shrink-0">
            <Upload size={22} className="text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="font-display font-semibold text-lg text-brand-ink">Upload from your device</p>
            <p className="text-sm text-brand-ink/70">JPG, PNG, WebP or GIF — up to 8 MB per image. Select one or many.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              data-testid="gallery-upload-kind"
              className="h-11 rounded-xl border-2 border-brand-ink px-3 bg-white font-bold text-sm"
            >
              <option value="gallery">Gallery</option>
              <option value="hero">Hero</option>
              <option value="about">About</option>
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => uploadFile(e.target.files)}
              className="hidden"
              data-testid="gallery-upload-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="gallery-upload-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green text-white font-bold px-5 h-11 btn-brutal"
            >
              {uploading ? (<><Loader2 size={16} className="animate-spin" /> Uploading…</>) : (<><Upload size={16} /> Choose files</>)}
            </button>
          </div>
        </div>
      </div>

      <p className="mt-6 mb-2 text-xs uppercase tracking-widest font-bold text-brand-ink/60">Or add by URL</p>
      <form onSubmit={submitAdd} className="rounded-3xl border-[3px] border-brand-ink bg-white p-5 shadow-brutal-sm grid gap-3 md:grid-cols-[1fr_180px_120px_120px_auto]" data-testid="gallery-add-form">
        <input
          type="url"
          required
          placeholder="Image URL (https://…)"
          value={draft.url}
          onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          data-testid="gallery-add-url"
          className="h-11 rounded-xl border-2 border-brand-ink px-3 focus:outline-none focus:ring-2 focus:ring-brand-red"
        />
        <input
          placeholder="Caption (optional)"
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          data-testid="gallery-add-label"
          className="h-11 rounded-xl border-2 border-brand-ink px-3 focus:outline-none focus:ring-2 focus:ring-brand-red"
        />
        <select
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
          data-testid="gallery-add-kind"
          className="h-11 rounded-xl border-2 border-brand-ink px-3 bg-white focus:outline-none focus:ring-2 focus:ring-brand-red"
        >
          <option value="gallery">Gallery</option>
          <option value="hero">Hero</option>
          <option value="about">About</option>
        </select>
        <input
          type="number"
          value={draft.position}
          onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) })}
          data-testid="gallery-add-position"
          className="h-11 rounded-xl border-2 border-brand-ink px-3 focus:outline-none focus:ring-2 focus:ring-brand-red"
        />
        <button
          type="submit"
          data-testid="gallery-add-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-red text-white font-bold px-5 h-11 btn-brutal"
        >
          <Plus size={16} /> Add
        </button>
      </form>

      {loading && <p className="mt-6 text-brand-ink/60">Loading…</p>}
      {!loading && shown.length === 0 && (
        <p className="mt-6 p-8 text-center text-brand-ink/60 rounded-3xl border-2 border-dashed border-brand-ink/40">No images yet in “{filter}”.</p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((it) => (
          <div key={it.id} className="rounded-3xl border-[3px] border-brand-ink bg-white overflow-hidden shadow-brutal-sm" data-testid={`gallery-item-${it.id}`}>
            <div className="aspect-video overflow-hidden bg-brand-cream">
              <img src={resolveUrl(it.url)} alt={it.label || "Gallery image"} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
            </div>
            <div className="p-4 space-y-2">
              {editing === it.id ? (
                <EditRow item={it} onSave={(patch) => saveEdit(it.id, patch)} onCancel={() => setEditing(null)} />
              ) : (
                <>
                  <p className="font-display font-semibold text-brand-ink text-sm break-all">{it.label || <em className="text-brand-ink/50">No caption</em>}</p>
                  <p className="text-xs text-brand-ink/60">Position: {it.position} {it.url.startsWith("/api/") && <span className="ml-1 rounded-full bg-brand-green/20 text-brand-greenDark px-1.5 py-0.5 text-[10px] font-bold">Uploaded</span>}</p>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setEditing(it.id)}
                      data-testid={`gallery-edit-${it.id}`}
                      className="rounded-full border-2 border-brand-ink bg-white px-3 py-1 text-xs font-bold hover:bg-brand-yellow/60"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(it.id)}
                      data-testid={`gallery-remove-${it.id}`}
                      className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-white px-3 py-1 text-xs font-bold text-brand-red hover:bg-brand-red hover:text-white transition-colors"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditRow({ item, onSave, onCancel }) {
  const [state, setState] = useState({
    url: item.url,
    label: item.label || "",
    kind: item.kind,
    position: item.position,
  });
  return (
    <div className="space-y-2" data-testid={`gallery-edit-form-${item.id}`}>
      <input
        value={state.url}
        onChange={(e) => setState({ ...state, url: e.target.value })}
        className="w-full h-10 rounded-lg border-2 border-brand-ink px-2 text-sm"
      />
      <input
        value={state.label}
        placeholder="Caption"
        onChange={(e) => setState({ ...state, label: e.target.value })}
        className="w-full h-10 rounded-lg border-2 border-brand-ink px-2 text-sm"
      />
      <div className="flex gap-2">
        <select
          value={state.kind}
          onChange={(e) => setState({ ...state, kind: e.target.value })}
          className="flex-1 h-10 rounded-lg border-2 border-brand-ink px-2 text-sm bg-white"
        >
          <option value="gallery">Gallery</option>
          <option value="hero">Hero</option>
          <option value="about">About</option>
        </select>
        <input
          type="number"
          value={state.position}
          onChange={(e) => setState({ ...state, position: Number(e.target.value) })}
          className="w-24 h-10 rounded-lg border-2 border-brand-ink px-2 text-sm"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onSave(state)}
          data-testid={`gallery-save-${item.id}`}
          className="inline-flex items-center gap-1 rounded-full bg-brand-green text-white font-bold px-3 py-1 text-xs border-2 border-brand-ink"
        >
          <Save size={12} /> Save
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1 rounded-full bg-white text-brand-ink font-bold px-3 py-1 text-xs border-2 border-brand-ink hover:bg-brand-yellow/60"
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}
